/**
 * CsvUploadModal
 * 홈택스 CSV 업로드 모달
 */

import React, { useState, useRef } from 'react';
import { CsvUploadResult } from '../../models/Tax';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (file: File) => Promise<CsvUploadResult | null>;
  onRefresh: () => void;
}

export const CsvUploadModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onUpload,
  onRefresh,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<CsvUploadResult | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setResult(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    const uploadResult = await onUpload(selectedFile);
    setUploading(false);

    if (uploadResult) {
      setResult(uploadResult);
      onRefresh();
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setResult(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">CSV 업로드</h3>
          <p className="text-sm text-gray-500 mt-1">
            홈택스에서 다운받은 카드 사용내역 CSV 파일을 업로드하세요
          </p>
        </div>

        <div className="px-6 py-4 space-y-4">
          {/* 파일 선택 */}
          <div>
            <input
              ref={fileRef}
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="hidden"
            />
            <div
              onClick={() => fileRef.current?.click()}
              className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
            >
              {selectedFile ? (
                <div>
                  <p className="text-sm font-medium text-gray-900">{selectedFile.name}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {(selectedFile.size / 1024).toFixed(1)} KB
                  </p>
                </div>
              ) : (
                <div>
                  <p className="text-gray-500">클릭하여 CSV 파일 선택</p>
                  <p className="text-xs text-gray-400 mt-1">EUC-KR, CP949, UTF-8 인코딩 지원</p>
                </div>
              )}
            </div>
          </div>

          {/* 안내 */}
          <div className="bg-blue-50 rounded-md p-3">
            <p className="text-xs text-blue-800 font-medium mb-1">홈택스 CSV 다운로드 방법:</p>
            <ol className="text-xs text-blue-700 list-decimal list-inside space-y-0.5">
              <li>홈택스 로그인 → 조회/발급 → 현금영수증/신용카드</li>
              <li>사업용 신용카드 매입세액 → 매입세액 공제확인/변경</li>
              <li>조회 기간 선택 → 조회 → 엑셀 다운로드</li>
            </ol>
          </div>

          {/* 업로드 결과 */}
          {result && (
            <div className={`rounded-md p-3 ${result.errors.length > 0 ? 'bg-yellow-50' : 'bg-green-50'}`}>
              <p className="text-sm font-medium">
                {result.errors.length > 0 ? '업로드 완료 (일부 오류)' : '업로드 완료'}
              </p>
              <div className="text-xs mt-1 space-y-0.5">
                <p className="text-green-700">추가: {result.inserted}건</p>
                {result.skipped > 0 && <p className="text-gray-600">건너뜀: {result.skipped}건</p>}
                {result.errors.length > 0 && (
                  <div className="mt-2">
                    <p className="text-red-600 font-medium">오류:</p>
                    {result.errors.slice(0, 5).map((err, i) => (
                      <p key={i} className="text-red-500">{err}</p>
                    ))}
                    {result.errors.length > 5 && (
                      <p className="text-red-400">... 외 {result.errors.length - 5}건</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 버튼 */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              {result ? '닫기' : '취소'}
            </button>
            {!result && (
              <button
                onClick={handleUpload}
                disabled={!selectedFile || uploading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {uploading ? '업로드 중...' : '업로드'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
